// src/pages/SchoolDashboard.jsx
//
// School-admin dashboard: KPI totals across every joined GEFEN
// program, a combined issued/redeemed trend over time, a per-program
// comparison, and a growth-trend sparkline per program. Mirrors the
// same chart language used in the school-area preview artifact
// (issued = indigo, redeemed = emerald, consistently across every
// chart on the page — color follows the entity, never per-program).
import React, { useEffect, useMemo, useState } from 'react';
import { useUser } from '../contexts/UserContext';
import { useSchoolPrograms } from '../contexts/SchoolProgramsContext';
import SpinnerOverlay from '../components/ui/SpinnerOverlay';

const formatMoney = (v) => `₪${Number(v || 0).toLocaleString('he-IL', { maximumFractionDigits: 0 })}`;

const StatTile = ({ label, value, accent = 'text-gray-800' }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-4 py-4 flex flex-col items-center justify-center">
    <span className={`text-lg font-bold ${accent}`}>{value}</span>
    <span className="text-xs text-gray-500 mt-1">{label}</span>
  </div>
);

// Two-series (issued/redeemed) SVG line chart with hover tooltip.
// compact=true renders a bare sparkline (no axis/legend/tooltip) for
// the per-program growth-trend cards.
const TrendLineChart = ({ data, compact = false, height = 160 }) => {
  const width = compact ? 220 : 600;
  const padL = compact ? 2 : 40;
  const padR = compact ? 2 : 12;
  const padT = compact ? 4 : 12;
  const padB = compact ? 4 : 24;
  const innerW = width - padL - padR;
  const innerH = height - padT - padB;
  const max = Math.max(1, ...data.flatMap((d) => [d.issuedAmount, d.redeemedAmount]));
  const stepX = data.length > 1 ? innerW / (data.length - 1) : 0;
  const pt = (i, v) => [padL + stepX * i, padT + innerH - (v / max) * innerH];
  const pathFor = (key) => data.map((d, i) => pt(i, d[key])).map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x},${y}`).join(' ');
  const [hoverI, setHoverI] = useState(null);

  if (data.length === 0) {
    return <p className="text-xs text-gray-400 py-6 text-center">אין עדיין נתונים להצגה.</p>;
  }

  return (
    <div className="relative">
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} preserveAspectRatio="none" onMouseLeave={() => setHoverI(null)}>
        {!compact && [0, 0.5, 1].map((f) => (
          <line key={f} x1={padL} x2={width - padR} y1={padT + innerH * (1 - f)} y2={padT + innerH * (1 - f)} stroke="#e5e7eb" strokeWidth="1" />
        ))}
        <path d={pathFor('issuedAmount')} fill="none" stroke="#4f46e5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d={pathFor('redeemedAmount')} fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {data.map((d, i) => {
          const [x1, y1] = pt(i, d.issuedAmount);
          const [x2, y2] = pt(i, d.redeemedAmount);
          return (
            <g key={d.month}>
              <rect
                x={Math.max(0, x1 - (stepX || innerW) / 2)}
                y={0}
                width={stepX || innerW}
                height={height}
                fill="transparent"
                onMouseEnter={() => setHoverI(i)}
              />
              <circle cx={x1} cy={y1} r={compact ? 2.5 : 4} fill="#4f46e5" stroke="#fff" strokeWidth="2" />
              <circle cx={x2} cy={y2} r={compact ? 2.5 : 4} fill="#059669" stroke="#fff" strokeWidth="2" />
              {!compact && <text x={x1} y={height - 6} fontSize="9.5" fill="#6b7280" textAnchor="middle">{d.month}</text>}
            </g>
          );
        })}
      </svg>
      {!compact && hoverI !== null && (
        <div
          className="absolute top-1 -translate-x-1/2 bg-white border border-gray-200 rounded-lg shadow-md px-2.5 py-1.5 text-[11px] pointer-events-none whitespace-nowrap"
          style={{ left: `${(pt(hoverI, 0)[0] / width) * 100}%` }}
        >
          <b className="block mb-0.5">{data[hoverI].month}</b>
          <div>הונפק: {formatMoney(data[hoverI].issuedAmount)}</div>
          <div>מומש: {formatMoney(data[hoverI].redeemedAmount)}</div>
        </div>
      )}
      {!compact && (
        <div className="flex gap-4 text-[11px] text-gray-500 mt-2">
          <span className="inline-flex items-center gap-1.5"><i className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: '#4f46e5' }} /> הונפק</span>
          <span className="inline-flex items-center gap-1.5"><i className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: '#059669' }} /> מומש</span>
        </div>
      )}
    </div>
  );
};

// Grouped-bar comparison across programs — issued (translucent) with
// redeemed (solid) overlaid on the same baseline, since redeemed is
// always a subset of issued. Programs are told apart by their x-axis
// label, so no per-program color is needed.
const ProgramCompareChart = ({ programs }) => {
  const max = Math.max(1, ...programs.flatMap((p) => [p.couponsIssuedAmount, p.couponsRedeemedAmount]), 1);
  const [hoverId, setHoverId] = useState(null);

  return (
    <div>
      <div className="relative flex items-end gap-3 flex-wrap" style={{ height: 150, paddingTop: 28 }}>
        {programs.map((p) => (
          <div
            key={p.schoolProgramId}
            className="relative flex-1 flex flex-col items-center gap-1.5 h-full"
            style={{ minWidth: 46 }}
            onMouseEnter={() => setHoverId(p.schoolProgramId)}
            onMouseLeave={() => setHoverId(null)}
          >
            {hoverId === p.schoolProgramId && (
              <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 -translate-y-full bg-white border border-gray-200 rounded-lg shadow-md px-2.5 py-1.5 text-[11px] whitespace-nowrap z-10">
                <b className="block mb-0.5">{p.programName}</b>
                <div>הונפק: {formatMoney(p.couponsIssuedAmount)}</div>
                <div>מומש: {formatMoney(p.couponsRedeemedAmount)}</div>
              </div>
            )}
            <div className="relative w-full flex-1 flex items-end justify-center" style={{ maxWidth: 34 }}>
              <div className="w-full rounded-t" style={{ height: `${(p.couponsIssuedAmount / max) * 100}%`, background: 'rgba(79,70,229,.25)' }} />
              <div className="absolute bottom-0 inset-x-0 mx-auto rounded-t" style={{ maxWidth: 34, height: `${(p.couponsRedeemedAmount / max) * 100}%`, background: '#059669' }} />
            </div>
            <div className="text-[10px] text-gray-500 max-w-[64px] truncate text-center" title={p.programName}>{p.programName}</div>
          </div>
        ))}
      </div>
      <div className="flex gap-4 text-[11px] text-gray-500 mt-2">
        <span className="inline-flex items-center gap-1.5"><i className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: 'rgba(79,70,229,.25)' }} /> הונפק</span>
        <span className="inline-flex items-center gap-1.5"><i className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: '#059669' }} /> מומש</span>
      </div>
    </div>
  );
};

const monthlyGrowthPct = (monthly) => {
  if (!monthly || monthly.length < 2) return null;
  const last = monthly[monthly.length - 1].issuedAmount;
  const prev = monthly[monthly.length - 2].issuedAmount;
  if (prev <= 0) return last > 0 ? 100 : null;
  return Math.round(((last - prev) / prev) * 100);
};

const SchoolDashboard = () => {
  const { showAlert } = useUser();
  const { fetchDashboardTrends } = useSchoolPrograms();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const result = await fetchDashboardTrends();
        if (!cancelled) setData(result);
      } catch (err) {
        showAlert?.(err.message || 'שגיאה בטעינת לוח הבקרה', 'warning');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const byProgram = data?.byProgram || [];
  const totals = data?.totals;
  const monthlyCombined = useMemo(() => data?.monthlyCombined || [], [data]);

  return (
    <div className="min-h-screen bg-gray-100" dir="rtl">
      <div className="mx-auto max-w-6xl px-3 sm:px-6 py-6 space-y-6">
        <div>
          <h1 className="text-lg sm:text-xl font-bold text-gray-800">לוח בקרה — תוכניות גפ״ן</h1>
          <p className="text-xs text-gray-500 mt-1">סקירה כללית, השוואת תוכניות ומגמות ניצול קופונים.</p>
        </div>

        {totals && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <StatTile label="תוכניות פעילות" value={totals.programCount} />
            <StatTile label="סה״כ תקציב מוקצה" value={formatMoney(totals.allocatedBudget)} />
            <StatTile label="סה״כ הונפק" value={formatMoney(totals.couponsIssuedAmount)} accent="text-indigo-700" />
            <StatTile label="סה״כ מומש" value={formatMoney(totals.couponsRedeemedAmount)} accent="text-emerald-700" />
            <StatTile label="תלמידים רשומים" value={totals.studentsEnrolled} />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-5">
            <h2 className="text-sm font-semibold text-gray-700">מגמת ניצול קופונים — כלל התוכניות</h2>
            <p className="text-xs text-gray-400 mt-0.5 mb-2">הונפק מול מומש לאורך זמן, מצטבר על פני כל התוכניות.</p>
            <TrendLineChart data={monthlyCombined} />
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-5">
            <h2 className="text-sm font-semibold text-gray-700">השוואת תוכניות</h2>
            <p className="text-xs text-gray-400 mt-0.5 mb-2">הונפק מול מומש, לפי תוכנית.</p>
            <ProgramCompareChart programs={byProgram} />
          </div>
        </div>

        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-gray-600">מגמת גידול לפי תוכנית</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {byProgram.map((p) => {
              const growth = monthlyGrowthPct(p.monthly);
              return (
                <div key={p.schoolProgramId} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="text-sm font-semibold text-gray-800">{p.programName}</h3>
                    {growth !== null && (
                      <span className={`text-[10.5px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${growth >= 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
                        {growth >= 0 ? '▲' : '▼'} {Math.abs(growth)}%
                      </span>
                    )}
                  </div>
                  <TrendLineChart data={p.monthly} compact height={54} />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {loading && <SpinnerOverlay />}
    </div>
  );
};

export default SchoolDashboard;